import { useMemo } from 'react'
import type { RefObject } from 'react'

import { styled } from 'styled-components'

import type {
  ListingFiltersState,
  ListingSearchContextValue,
} from '../ListingSearchProvider'
import { StagedThresholdPanel } from './filters/CategoryFilter/CategoryThresholdControl'
import FilterContainer, { FilterList } from './shared/FilterContainer'
import type { ListingFilter, SelectedFilterType } from './types'

// Spacing between the threshold panel and the options below it.
const ThresholdPanel = styled(StagedThresholdPanel)`
  margin-bottom: 1rem;
`

type FiltersModalContentProps<T> = {
  containerRef?: RefObject<HTMLDivElement | null>
  filterShouldDisplaySeparately: boolean
  filters: ListingFilter<T>[]
  filterValues: Partial<ListingFiltersState>
  onFilterChange: (key: keyof ListingFiltersState, values: T) => void
  onFilterError: (value: boolean) => void
  searchCtx: ListingSearchContextValue
  selectedFilter: string
  selectFilter: (filter: SelectedFilterType) => void
}

export const FiltersModalContent = <T,>({
  containerRef,
  filterShouldDisplaySeparately,
  filters,
  filterValues,
  onFilterChange,
  onFilterError,
  searchCtx,
  selectedFilter,
  selectFilter,
}: FiltersModalContentProps<T>) => {
  // Create merged context with local filter values so getOwnProps uses the modal's
  // locally edited values instead of the globally applied filters
  const localSearchCtx = useMemo<ListingSearchContextValue>(
    () => ({
      ...searchCtx,
      state: {
        ...searchCtx.state,
        filters: { ...searchCtx.state.filters, ...filterValues },
      },
    }),
    [searchCtx, filterValues]
  )

  const handleClick = (target: HTMLButtonElement) => {
    if (containerRef?.current) {
      containerRef.current.scrollTo({
        top: target.offsetTop - 84,
        behavior: 'smooth',
      })
    }
  }

  if (selectedFilter && filterShouldDisplaySeparately) {
    const FilterComp = filters.find((Filter) => Filter.label === selectedFilter)

    if (!FilterComp) {
      throw new Error(
        `Unable to find component with name "${selectedFilter}" in "filters".`
      )
    }
    const filterProps = FilterComp.getOwnProps(localSearchCtx)

    const handleChange = (value: T) => {
      onFilterChange(
        filterProps.filterKey,
        (value instanceof Set ? [...value] : value) as T
      )
    }

    return (
      // oxlint-disable-next-line react/static-components
      <FilterComp
        {...filterProps}
        desktopScreen={false}
        onChange={handleChange}
      />
    )
  } else {
    return (
      <FilterList>
        {filters.map((FilterComp) => {
          const label = FilterComp.label
          const filterProps = FilterComp.getOwnProps(localSearchCtx)

          const shouldDisplayFilter =
            (FilterComp.isAlwaysDisplayed ||
              Object.keys(filterProps.value || {}).length ||
              Object.keys(filterProps.items || {}).length) &&
            // The search-query filter has no widget of its own (it's driven by the
            // top nav / storefront search box), so it's excluded here just like the
            // desktop sidebar excludes it in SearchFilters.tsx.
            FilterComp.label !== 'Filters.searchQuery'

          const handleChange = (value: T) => {
            onFilterChange(
              filterProps.filterKey,
              (value instanceof Set ? [...value] : value) as T
            )
          }

          const handleExpand = () => {
            selectFilter({
              filterLabel: label,
              itemsCount: filterProps.items
                ? Object.keys(filterProps.items).length
                : 0,
            })
          }

          return shouldDisplayFilter ? (
            <FilterContainer
              key={FilterComp.label}
              afterExpandAnimation={handleClick}
              expandedOnInit={false}
              label={FilterComp.label}
              onExpand={handleExpand}
              shouldUseExpandMemory={false}
              tooltip={FilterComp.tooltip}
            >
              {FilterComp.hasCategoryThreshold && (
                <ThresholdPanel
                  filterValues={filterValues}
                  onChange={(step) =>
                    onFilterChange('categoryThreshold', step as T)
                  }
                />
              )}
              <FilterComp
                {...filterProps}
                desktopScreen={false}
                onChange={handleChange}
                onError={onFilterError}
              />
            </FilterContainer>
          ) : null
        })}
      </FilterList>
    )
  }
}
